export type SpecialEventGuestNamesMap = Record<string, string[]>;

export const normalizeProgramGuestNames = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    const trimmed = value.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return normalizeProgramGuestNames(parsed);
        }
      } catch {
        // fall through to single guest name
      }
    }
    return [trimmed];
  }
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const numericKeys = Object.keys(obj)
      .filter((key) => /^\d+$/.test(key))
      .sort((a, b) => Number(a) - Number(b));
    if (numericKeys.length > 0) {
      return numericKeys
        .map((key) => String(obj[key]).trim())
        .filter(Boolean);
    }
  }
  return [];
};

export const normalizeSpecialEventGuestNames = (
  raw: unknown,
): SpecialEventGuestNamesMap => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }

  const result: SpecialEventGuestNamesMap = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const guests = normalizeProgramGuestNames(value);
    if (guests.length > 0) {
      result[key] = guests;
    }
  }
  return result;
};

const findMatchingProgramKey = (
  normalized: SpecialEventGuestNamesMap,
  programId: string,
): string | undefined => {
  if (!programId) {
    return undefined;
  }

  if (normalized[programId]) {
    return programId;
  }

  const programIdString = String(programId);
  if (normalized[programIdString]) {
    return programIdString;
  }

  const target = programIdString.trim();
  for (const key of Object.keys(normalized)) {
    if (key.trim() === target) {
      return key;
    }
  }

  return undefined;
};

export const getSpecialEventGuestsForProgram = (
  guestNames: SpecialEventGuestNamesMap | Record<string, string | string[]>,
  programId: string,
): string[] => {
  const normalized = normalizeSpecialEventGuestNames(guestNames);
  const matchingKey = findMatchingProgramKey(normalized, programId);
  return matchingKey ? normalized[matchingKey] : [];
};

export const addSpecialEventGuest = (
  guestNames: SpecialEventGuestNamesMap,
  programId: string,
  name: string,
): SpecialEventGuestNamesMap => {
  const trimmed = name.trim();
  if (!trimmed) {
    return guestNames;
  }

  const existing = guestNames[programId] ?? [];
  return {
    ...guestNames,
    [programId]: [...existing, trimmed],
  };
};

export const removeSpecialEventGuest = (
  guestNames: SpecialEventGuestNamesMap,
  programId: string,
  guestIndex: number,
): SpecialEventGuestNamesMap => {
  const existing = guestNames[programId] ?? [];
  if (guestIndex < 0 || guestIndex >= existing.length) {
    return guestNames;
  }

  const nextGuests = existing.filter((_, index) => index !== guestIndex);
  const next = { ...guestNames };
  if (nextGuests.length > 0) {
    next[programId] = nextGuests;
  } else {
    delete next[programId];
  }
  return next;
};

export const serializeSpecialEventGuestNames = (
  guestNames: SpecialEventGuestNamesMap,
): Record<string, string[]> | undefined => {
  const entries = Object.entries(guestNames).filter(
    ([, names]) => names.length > 0,
  );
  if (entries.length === 0) {
    return undefined;
  }
  return Object.fromEntries(entries);
};

export const expandSpecialEventGuestRow = (row: {
  guestName?: unknown;
  mainAttendeeName?: string;
  firm?: string;
  jurisdiction?: string;
}): Array<{
  guestName: string;
  mainAttendeeName: string;
  firm: string;
  jurisdiction: string;
}> => {
  const names = normalizeProgramGuestNames(row.guestName);
  return names.map((guestName) => ({
    guestName,
    mainAttendeeName: row.mainAttendeeName ?? "",
    firm: row.firm ?? "",
    jurisdiction: row.jurisdiction ?? "",
  }));
};

const normalizeSpecialEventSegment = <
  TAttendee extends {
    name?: string;
    firm?: string;
    jurisdiction?: string;
    guests?: number;
  },
  TGuest extends {
    guestName?: unknown;
    mainAttendeeName?: string;
    firm?: string;
    jurisdiction?: string;
  },
>(
  attendeesInput: TAttendee[] | undefined,
  guestsInput: TGuest[] | undefined,
) => {
  const expandedGuests = (guestsInput ?? []).flatMap(expandSpecialEventGuestRow);
  const guestCountByAttendee = new Map<string, number>();

  for (const guest of expandedGuests) {
    const key = guest.mainAttendeeName;
    guestCountByAttendee.set(key, (guestCountByAttendee.get(key) ?? 0) + 1);
  }

  const attendees = (attendeesInput ?? []).map((attendee) => {
    const counted = guestCountByAttendee.get(attendee.name ?? "") ?? 0;
    return {
      ...attendee,
      guests:
        counted > 0
          ? counted
          : typeof attendee.guests === "number"
            ? attendee.guests
            : 0,
    };
  });

  return {
    attendees,
    guests: expandedGuests,
    guestsCount: expandedGuests.length,
    attendeesCount: attendees.length,
  };
};

export const normalizeSpecialEventDashboardEntry = <
  T extends {
    name?: string;
    attendeesCount?: number;
    guestsCount?: number;
    staffAttendeesCount?: number;
    staffGuestsCount?: number;
    attendees?: Array<{
      name?: string;
      firm?: string;
      jurisdiction?: string;
      guests?: number;
    }>;
    guests?: Array<{
      guestName?: unknown;
      mainAttendeeName?: string;
      firm?: string;
      jurisdiction?: string;
    }>;
    staffAttendees?: Array<{
      name?: string;
      firm?: string;
      jurisdiction?: string;
      guests?: number;
    }>;
    staffGuests?: Array<{
      guestName?: unknown;
      mainAttendeeName?: string;
      firm?: string;
      jurisdiction?: string;
    }>;
  },
>(
  entry: T,
): T => {
  const regular = normalizeSpecialEventSegment(entry.attendees, entry.guests);
  const staff = normalizeSpecialEventSegment(
    entry.staffAttendees,
    entry.staffGuests,
  );

  return {
    ...entry,
    guests: regular.guests,
    guestsCount: regular.guestsCount,
    attendeesCount: regular.attendeesCount,
    attendees: regular.attendees,
    staffGuests: staff.guests,
    staffGuestsCount: staff.guestsCount,
    staffAttendeesCount: staff.attendeesCount,
    staffAttendees: staff.attendees,
  };
};
